import Vue from "vue";
import Vuex from "vuex";
import {
  storage,
  usersCollection,
  imagesCollection,
  websitesCollection,
  caseStudyCollection
} from "../firebase-config";

Vue.use(Vuex);

const imageModule = {
  namespaced: true,

  state: () => ({
    images: [],
    totalDocuments: null,
    busy: false
  }),
  mutations: {
    addImages(state, val) {
      const existingIDs = [];

      for (let image of state.images) {
        existingIDs.push(image.id);
      }

      const newImages = val.filter(val => !existingIDs.includes(val.id));
      state.images.unshift(...newImages);
    },
    setInitialImages(state, val) {
      state.images = val ? val : [];
    },
    setDocCount(state, val) {
      state.totalDocuments = val;
    },
    setBusy(state, val) {
      state.busy = val;
    },
    modifyImage(state, val) {
      const existingImageIndex = state.images.findIndex(
        image => image.id === val.id
      );
      if (existingImageIndex >= 0) {
        state.images.splice(existingImageIndex, 1, val);
      }
    },
    removeImage(state, val) {
      const updatedImages = state.images.filter(image => image.id !== val.id);
      state.images = updatedImages;
    }
  },
  actions: {
    async countImages({ commit }) {
      let storageRef = storage.ref();
      try {
        const allDocuments = await storageRef.child(`images`).listAll();
        commit("setDocCount", allDocuments.items.length);
      } catch (err) {
        console.error("countImages: " + err.message);
      }
    },
    async fetchImages({ commit, state, rootState }) {
      const lastVisibile = state.images.slice(-1)[0];
      if (state.images.length < state.totalDocuments) {
        try {
          commit("setBusy", true);
          await imagesCollection
            .orderBy("createdOn", "desc")
            .limit(rootState.limit)
            .startAfter(lastVisibile.createdOn)
            .onSnapshot(
              querySnapshot => {
                if (querySnapshot.docChanges()[0].type == "modified") {
                  let image = querySnapshot.docChanges()[0].doc.data();
                  image.id = querySnapshot.docChanges()[0].doc.id;
                  return commit("modifyImage", image);
                } else if (querySnapshot.docChanges()[0].type == "removed") {
                  let image = querySnapshot.docChanges()[0].doc.data();
                  image.id = querySnapshot.docChanges()[0].doc.id;
                  return commit("removeImage", image);
                } else {
                  let imagesArray = [];
                  querySnapshot.docs.forEach(doc => {
                    let image = doc.data();
                    image.id = doc.id;
                    imagesArray.push(image);
                  });
                  commit("setBusy", false);
                  commit("addImages", imagesArray);
                }
              },
              function(error) {
                throw error;
              }
            );
        } catch (err) {
          commit("setBusy", false);
          console.error(err);
        }
      }
    },
    async saveInitial({ commit, state, rootState }) {
      if (state.images.length <= state.totalDocuments) {
        try {
          await imagesCollection
            .orderBy("createdOn", "desc")
            .limit(rootState.limit)
            .onSnapshot(
              querySnapshot => {
                let changes = querySnapshot.docChanges();
                changes.forEach(change => {
                  let image;
                  let imagesArray;
                  switch (change.type) {
                    case "modified":
                      image = change.doc.data();
                      image.id = change.doc.id;
                      commit("modifyImage", image);
                      break;
                    case "removed":
                      image = change.doc.data();
                      image.id = change.doc.id;
                      commit("removeImage", image);
                      break;
                    default:
                      imagesArray = [];
                      querySnapshot.docs.forEach(doc => {
                        let image = doc.data();
                        image.id = doc.id;
                        imagesArray.push(image);
                      });
                      commit("addImages", imagesArray);
                  }
                });
              },
              function(error) {
                throw error;
              }
            );
        } catch (err) {
          console.error(err);
        }
      }
    }
  },
  getters: {
    busy(state) {
      return state.busy;
    },
    images(state) {
      return state.images.sort((a, b) => {
        return b.createdOn - a.createdOn;
      });
    },
    totalDocuments(state) {
      return state.totalDocuments;
    }
  }
};

const websiteModule = {
  namespaced: true,
  state: () => ({
    websites: [],
    busy: false
  }),
  mutations: {
    addWebsites(state, val) {
      const existingIDs = [];

      for (let website of state.websites) {
        existingIDs.push(website.id);
      }

      const newWebsites = val.filter(val => !existingIDs.includes(val.id));

      state.websites.unshift(...newWebsites);
    },
    setBusy(state, val) {
      state.busy = val;
    },
    setInitialWebsites(state, val) {
      state.websites = val ? val : [];
    },
    modifyWebsite(state, val) {
      const existingWebsiteIndex = state.websites.findIndex(
        website => website.id === val.id
      );
      if (existingWebsiteIndex >= 0) {
        state.websites.splice(existingWebsiteIndex, 1, val);
      }
    },
    removeWebsite(state, val) {
      const updatedWebsites = state.websites.filter(
        website => website.id !== val.id
      );
      state.websites = updatedWebsites;
    }
  },
  actions: {
    async fetchWebsites({ commit, state, rootState }) {
      const lastVisibile = state.websites.slice(-1)[0];
      if (lastVisibile)
        try {
          commit("setBusy", true);
          await websitesCollection
            .orderBy("createdOn", "desc")
            .limit(rootState.limit)
            .startAfter(lastVisibile.createdOn)
            .onSnapshot(
              querySnapshot => {
                if (querySnapshot.docChanges().length == 0) {
                  return commit("setBusy", true);
                }
                if (querySnapshot.docChanges()[0].type == "modified") {
                  let website = querySnapshot.docChanges()[0].doc.data();
                  website.id = querySnapshot.docChanges()[0].doc.id;
                  return commit("modifyWebsite", website);
                } else if (querySnapshot.docChanges()[0].type == "removed") {
                  let website = querySnapshot.docChanges()[0].doc.data();
                  website.id = querySnapshot.docChanges()[0].doc.id;
                  return commit("removeWebsite", website);
                } else {
                  let websiteArray = [];
                  querySnapshot.docs.forEach(doc => {
                    let website = doc.data();
                    website.id = doc.id;
                    websiteArray.push(website);
                  });
                  commit("setBusy", false);
                  commit("addWebsites", websiteArray);
                }
              },
              function(error) {
                throw error;
              }
            );
        } catch (err) {
          commit("setBusy", false);
          console.error(err);
        }
    },
    async saveInitial({ commit, rootState }) {
      try {
        await websitesCollection
          .orderBy("createdOn", "desc")
          .limit(rootState.limit)
          .onSnapshot(
            querySnapshot => {
              let changes = querySnapshot.docChanges();
              changes.forEach(change => {
                let website;
                let websiteArray;
                switch (change.type) {
                  case "modified":
                    website = change.doc.data();
                    website.id = change.doc.id;
                    commit("modifyWebsite", website);
                    break;
                  case "removed":
                    website = change.doc.data();
                    website.id = change.doc.id;
                    commit("removeWebsite", website);
                    break;
                  default:
                    websiteArray = [];
                    querySnapshot.docs.forEach(doc => {
                      let website = doc.data();
                      website.id = doc.id;
                      websiteArray.push(website);
                    });
                    commit("addWebsites", websiteArray);
                }
              });
            },
            function(error) {
              throw error;
            }
          );
      } catch (err) {
        console.error(err);
      }
    }
  },
  getters: {
    websites(state) {
      return state.websites;
    },
    busy(state) {
      return state.busy;
    }
  }
};

const caseStudyModule = {
  namespaced: true,
  state: () => ({
    caseStudies: [],
    busy: false,
    isLoading: false,
    caseStudy: {
      image: "",
      url: "",
      name: ""
    }
  }),
  mutations: {
    addCaseStudies(state, val) {
      const existingIDs = [];

      for (let caseStudy of state.caseStudies) {
        existingIDs.push(caseStudy.id);
      }

      const newCases = val.filter(val => !existingIDs.includes(val.id));

      state.caseStudies.unshift(...newCases);
    },
    setBusy(state, val) {
      state.busy = val;
    },
    setInitialCaseStudies(state, val) {
      state.caseStudies = val ? val : [];
    },
    modifyCaseStudy(state, val) {
      const existingCaseStudyIndex = state.caseStudies.findIndex(
        caseStudy => caseStudy.id === val.id
      );
      if (existingCaseStudyIndex >= 0) {
        state.caseStudies.splice(existingCaseStudyIndex, 1, val);
      }
    },
    removeCaseStudy(state, val) {
      const updatedCaseStudies = state.caseStudies.filter(
        caseStudy => caseStudy.id !== val.id
      );
      state.caseStudies = updatedCaseStudies;
    },
    getCaseStudy(state, slug) {
      state.caseStudy = state.caseStudies.find(
        caseStudy => caseStudy.slug === slug
      );
    },
    loadingState(state, boolean) {
      state.isLoading = boolean;
    }
  },
  actions: {
    async fetchCaseStudies({ commit, state, rootState }) {
      const lastVisibile = state.caseStudies.slice(-1)[0];
      if (lastVisibile)
        try {
          commit("setBusy", true);
          await caseStudyCollection
            .orderBy("createdOn", "desc")
            .limit(rootState.limit)
            .startAfter(lastVisibile.createdOn)
            .onSnapshot(
              querySnapshot => {
                if (querySnapshot.docChanges().length == 0) {
                  return commit("setBusy", true);
                }
                if (querySnapshot.docChanges()[0].type == "modified") {
                  let caseStudy = querySnapshot.docChanges()[0].doc.data();
                  caseStudy.id = querySnapshot.docChanges()[0].doc.id;
                  return commit("modifyCaseStudy", caseStudy);
                } else if (querySnapshot.docChanges()[0].type == "removed") {
                  let caseStudy = querySnapshot.docChanges()[0].doc.data();
                  caseStudy.id = querySnapshot.docChanges()[0].doc.id;
                  return commit("removeCaseStudy", caseStudy);
                } else {
                  let caseStudyArray = [];
                  querySnapshot.docs.forEach(doc => {
                    let caseStudy = doc.data();
                    caseStudy.id = doc.id;
                    caseStudy.slug = doc
                      .data()
                      .name.toLowerCase()
                      .replace(" ", "-");
                    caseStudyArray.push(caseStudy);
                    caseStudyArray.push(caseStudy);
                  });
                  commit("setBusy", false);
                  commit("addCaseStudies", caseStudyArray);
                }
              },
              function(error) {
                throw error;
              }
            );
        } catch (err) {
          commit("setBusy", false);
          console.error(err);
        }
    },
    async saveInitial({ commit, rootState }) {
      try {
        await caseStudyCollection
          .orderBy("createdOn", "desc")
          .limit(rootState.limit)
          .onSnapshot(
            querySnapshot => {
              let changes = querySnapshot.docChanges();
              changes.forEach(change => {
                let caseStudy;
                let caseStudyArray;
                switch (change.type) {
                  case "modified":
                    caseStudy = change.doc.data();
                    caseStudy.id = change.doc.id;
                    commit("modifyCaseStudy", caseStudy);
                    break;
                  case "removed":
                    caseStudy = change.doc.data();
                    caseStudy.id = change.doc.id;
                    commit("removeCaseStudy", caseStudy);
                    break;
                  default:
                    caseStudyArray = [];
                    querySnapshot.docs.forEach(doc => {
                      let caseStudy = doc.data();
                      caseStudy.id = doc.id;
                      caseStudy.slug = doc
                        .data()
                        .name.toLowerCase()
                        .replace(" ", "-");
                      caseStudyArray.push(caseStudy);
                    });
                    commit("addCaseStudies", caseStudyArray);
                }
              });
            },
            function(error) {
              throw error;
            }
          );
      } catch (err) {
        console.error(err);
      }
    },
    async getCaseStudy({ commit }, slug) {
      commit("loadingState", true);
      setTimeout(() => {
        commit("getCaseStudy", slug);
        commit("loadingState", false);
      }, 2000);
    }
  },
  getters: {
    caseStudies(state) {
      return state.caseStudies;
    },
    isLoading(state) {
      return state.isLoading;
    },
    busy(state) {
      return state.busy;
    },
    caseStudy(state) {
      return state.caseStudy;
    }
  }
};

export default new Vuex.Store({
  state: {
    currentUser: null,
    userProfile: null,
    limit: 10,
    allowsPushNotification: null
  },
  mutations: {
    setCurrentUser(state, val) {
      state.currentUser = val;
    },
    setUserProfile(state, val) {
      state.userProfile = val;
    },
    setNotificationStatus(state, val) {
      state.allowsPushNotification = val;
    }
  },
  actions: {
    async fetchUserProfile({ commit, state, dispatch }) {
      try {
        const res = await usersCollection.doc(state.currentUser.uid).get();
        commit("setUserProfile", res.data());
      } catch (err) {
        console.log(err);
        dispatch("clearData");
      }
    },
    setNotificationStatus({ commit }, status) {
      commit("setNotificationStatus", status);
    },
    clearData({ commit }) {
      commit("setUserProfile", {});
      commit("setCurrentUser", null);
    }
  },
  modules: {
    images: imageModule,
    websites: websiteModule,
    caseStudies: caseStudyModule
  },
  getters: {
    currentUser(state) {
      return state.currentUser;
    },
    userProfile(state) {
      return state.userProfile;
    },
    notificationStatus(state) {
      return state.allowsPushNotification;
    }
  }
});
